import React, { useState, useMemo, useCallback } from 'react'
import {
  Container,
  Header,
  Button,
  SpaceBetween,
  Box,
  Alert,
  Spinner
} from '@cloudscape-design/components'
import { useQuery } from '@tanstack/react-query'
import { useAction } from 'convex/react'
import { api } from '../../../../../../convex/_generated/api'
import { useAmplifyAuth } from '../../../../../providers/AmplifyAuthProvider'
import { useDashboardContext, getTimeRangeDates } from '../../../contexts/DashboardContext'
import { DashboardWidget } from '../../../types'
import { SimpleChart } from './SimpleChart'
import { ChartWidgetConfig } from './ChartWidgetConfig'

interface ChartWidgetProps {
  widget: DashboardWidget
  onEdit?: (widget: DashboardWidget) => void
  isEditing?: boolean
  onLegendRender?: (legendComponent: React.ReactNode) => void
}

// Helper function to get optimal query type based on time range
const getOptimalQueryType = (timeRange: { start: Date; end: Date }, dataType: 'current' | 'historical' | 'trends' = 'historical') => {
  if (dataType === 'current') return 'GET_PROPERTY_VALUE';
  if (dataType === 'trends') return 'GET_PROPERTY_VALUE_AGGREGATES';
  
  const duration = timeRange.end.getTime() - timeRange.start.getTime();
  const hours = duration / (1000 * 60 * 60);
  
  // Use interpolated for longer time ranges to reduce data points
  return hours > 24 ? 'GET_INTERPOLATED_PROPERTY_VALUES' : 'GET_PROPERTY_VALUE_HISTORY';
}

// Helper function to get optimal resolution based on time range
const getOptimalResolution = (startDate: Date, endDate: Date) => {
  const durationMs = endDate.getTime() - startDate.getTime();
  const durationHours = durationMs / (1000 * 60 * 60);
  
  // AWS SiteWise only supports: 1m, 15m, 1h, 1d
  if (durationHours <= 6) return 'Minute';        // Up to 6 hours: use 1 minute
  if (durationHours <= 168) return '15 Minutes';  // Up to 1 week: use 15 minutes  
  if (durationHours <= 720) return 'Hour';        // Up to 1 month: use 1 hour
  return 'Day';                                    // More than 1 month: use 1 day
}

export const ChartWidget: React.FC<ChartWidgetProps> = ({
  widget,
  onEdit,
  isEditing = false,
  onLegendRender
}) => {
  const [showConfig, setShowConfig] = useState(false)
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())
  
  const handleSeriesToggle = useCallback((seriesId: string) => {
    console.log('Toggling series:', seriesId)
    setHiddenSeries(prev => {
      const newSet = new Set(prev)
      if (newSet.has(seriesId)) {
        newSet.delete(seriesId)
        console.log('Showing series:', seriesId)
      } else {
        newSet.add(seriesId)
        console.log('Hiding series:', seriesId)
      }
      console.log('New hidden series:', Array.from(newSet))
      return newSet
    })
  }, [])
  
  const handleLegendClick = handleSeriesToggle
  
  // Dashboard context for time range and refresh triggers
  const { timeRange, refreshTrigger, setIsRefreshing, setTimeRange } = useDashboardContext()
  const { sessionId } = useAmplifyAuth()
  
  // Convert time range to dates
  const dates = useMemo(() => getTimeRangeDates(timeRange), [timeRange])

  // Handle chart zoom - updates dashboard time range (Grafana-style)
  const handleChartZoom = useCallback((newTimeRange: { start: Date; end: Date }) => {
    // Validate time range before setting
    const now = new Date()
    const startDate = new Date(newTimeRange.start)
    let endDate = new Date(newTimeRange.end)
    
    // Clamp end date to current time if it's in the future
    if (endDate > now) {
      console.log(`📅 Chart zoom: Clamping future end date ${endDate.toISOString()} to now: ${now.toISOString()}`)
      endDate = now
    }
    
    // Ensure start is before end
    if (startDate >= endDate) {
      console.log(`📅 Chart zoom: Start date ${startDate.toISOString()} is after end date, adjusting`)
      startDate.setTime(endDate.getTime() - 60 * 60 * 1000) // Set start to 1 hour before end
    }
    
    // Update global dashboard time range (affects ALL charts)
    setTimeRange({
      type: 'absolute',
      absolute: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      }
    })
  }, [setTimeRange])
  
  // Build SiteWise queries from widget configuration - respect explicit user choices
  const siteWiseQueries = useMemo(() => {
    if (!widget.queries || widget.queries.length === 0) return []
    
    return widget.queries
      .filter(query => {
        // Only include queries with valid Convex asset and property IDs
        const hasValidAsset = query.asset?.id && query.asset.id.length > 0
        const hasValidProperty = query.property?.id && query.property.id.length > 0
        
        if (!hasValidAsset || !hasValidProperty) {
          return false
        }
        return true
      })
      .map(query => {
        // Check if user has explicitly configured SiteWise query settings
        const hasExplicitConfig = query.siteWiseQuery && query.siteWiseQuery.queryType
        
        if (hasExplicitConfig) {
          // Use user's explicit configuration
          return {
            sessionId,
            asset: query.asset.external_asset_id, // Use SiteWise UUID directly
            property: query.property.external_property_id, // Use SiteWise UUID directly
            startDate: dates.start.toISOString(),
            endDate: dates.end.toISOString(),
            queryType: query.siteWiseQuery.queryType,
            quality: query.siteWiseQuery.quality || 'ANY',
            timeOrdering: query.siteWiseQuery.timeOrdering || 'ASCENDING',
            resolution: query.siteWiseQuery.resolution || 'Auto',
            aggregate: query.siteWiseQuery.aggregate,
            maxResults: query.siteWiseQuery.maxResults || 1000,
            // Store query metadata for tracking
            _queryMeta: {
              assetName: query.asset.name,
              propertyName: query.property.name,
              isExplicit: true
            }
          }
        } else {
          // Use default explicit query settings for widgets without explicit configuration
          return {
            sessionId,
            asset: query.asset.external_asset_id,      // SiteWise UUID
            property: query.property.external_property_id, // SiteWise UUID
            startDate: dates.start.toISOString(),
            endDate: dates.end.toISOString(),
            queryType: 'GET_PROPERTY_VALUE_AGGREGATES' as const, // Default to aggregates
            quality: 'ANY' as const,
            timeOrdering: 'ASCENDING' as const,
            resolution: 'Auto' as const,
            aggregate: 'Average' as const,
            maxResults: 3000, // Increased default - allows up to 12 pages of data
            _queryMeta: {
              assetName: query.asset.name,
              propertyName: query.property.name,
              isExplicit: true // Now everything is explicit
            }
          }
        }
      })
  }, [widget.queries, dates, sessionId])
  
  // Get Convex actions - batch API for performance
  const executeSiteWiseQueryAction = useAction(api["components/sitewise/index"].executeSiteWiseQuery)
  const executeBatchSiteWiseQueriesAction = useAction(api["components/sitewise/index"].executeBatchSiteWiseQueries)
  
  // Use TanStack Query for caching and deduplication
  const { data: response, isLoading, error, refetch } = useQuery({
    queryKey: ['sitewise-batch-queries', sessionId, siteWiseQueries, refreshTrigger],
    queryFn: async () => {
      if (!sessionId || siteWiseQueries.length === 0) return null
      
      // Check if we should use batch API based on query count AND time range
      const timeDurationMs = dates.end.getTime() - dates.start.getTime()
      const timeDurationHours = timeDurationMs / (1000 * 60 * 60)
      
      // Use individual queries for long time ranges (>2 hours) to avoid memory issues
      // Batch API has auto-switching that can return too much raw data
      const isLongTimeRange = timeDurationHours > 2
      const shouldUseBatch = siteWiseQueries.length > 1 && !isLongTimeRange
      
      
      if (shouldUseBatch) {
        // Transform to batch format
        const batchQueries = siteWiseQueries.map((query, index) => {
          
          return {
            entryId: `widget_${widget.id}_query_${index}`,
            assetId: query.asset,      // Direct field, not nested
            propertyId: query.property, // Direct field, not nested  
            startDate: dates.start.toISOString(),
            endDate: dates.end.toISOString(),
            queryType: query.queryType || getOptimalQueryType(dates),
            resolution: query.resolution || 'Auto',
            aggregate: query.aggregate || 'Average',
            quality: query.quality || 'ANY',
            maxResults: query.maxResults || 3000
          }
        })
        
        
        try {
          const result = await executeBatchSiteWiseQueriesAction({ 
            sessionId, 
            queries: batchQueries 
          })
          
          return {
            dataStreams: result?.dataStreams || [],
            viewport: {
              start: dates.start.toISOString(),
              end: dates.end.toISOString()
            },
            meta: {
              queryType: 'BATCH_OPTIMIZED',
              totalPoints: result?.meta?.totalPoints || 0,
              executionTime: result?.meta?.executionTime || 0,
              batchCount: result?.meta?.batchCount || 0
            }
          }
        } catch (batchError) {
          console.warn('⚠️ Batch API failed, falling back to individual queries:', batchError)
          // Fall back to individual queries
        }
      }
      
      // Fallback: Execute queries individually 
      const allDataStreams: any[] = []
      let totalExecutionTime = 0
      
      
      for (const query of siteWiseQueries) {
        try {
          const result = await executeSiteWiseQueryAction({ query })
          if (result?.dataStreams) {
            allDataStreams.push(...result.dataStreams)
          }
          totalExecutionTime += result?.meta?.executionTime || 0
        } catch (error) {
          console.error(`❌ Failed query for ${query._queryMeta.propertyName}:`, error)
        }
      }
      
      // Return combined response
      return {
        dataStreams: allDataStreams,
        viewport: {
          start: dates.start.toISOString(),
          end: dates.end.toISOString()
        },
        meta: {
          queryType: 'INDIVIDUAL_FALLBACK',
          totalPoints: allDataStreams.reduce((sum: number, stream: any) => sum + (stream.data?.length || 0), 0),
          executionTime: totalExecutionTime
        }
      }
    },
    enabled: !!sessionId && siteWiseQueries.length > 0,
    staleTime: 60000, // Cache for 1 minute (longer for batch queries)
    gcTime: 300000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when switching back to browser tab
    refetchOnReconnect: true, // Refetch when connection is restored
  })
  
  // Transform SiteWise data streams to chart format
  const dataStreams = useMemo(() => {
    if (!response?.dataStreams) return []
    
    
    const transformed = response.dataStreams.map((stream, index) => {
      // Try to find the corresponding query metadata for better naming
      const correspondingQuery = siteWiseQueries[index]
      const assetName = correspondingQuery?._queryMeta?.assetName
      const propertyName = correspondingQuery?._queryMeta?.propertyName
      
      // Find the widget query configuration (contains yAxis and color)
      const widgetQuery = widget.queries?.[index]
      
      // Create enhanced series name: "Asset Name - Property Name" or fallback to stream.name
      let enhancedName = stream.name
      if (assetName && propertyName) {
        enhancedName = `${assetName} - ${propertyName}`
      } else if (propertyName) {
        enhancedName = propertyName
      }
      
      // Y-axis and color mapping working correctly now
      
      return {
        id: stream.id,
        name: enhancedName,
        color: widgetQuery?.color || stream.color, // Use widget query color first, then stream color
        unit: stream.unit || '',
        yAxis: widgetQuery?.yAxis || 'left', // Add Y-axis assignment from widget query
        data: stream.data.map(point => ({
          timestamp: point.x,
          value: point.y
        }))
      }
    })
    
    // Debug analysis removed for performance - was causing UI freezing
    
    return transformed
  }, [response])
  
  
  // Handle loading state changes for dashboard context
  React.useEffect(() => {
    setIsRefreshing(isLoading)
  }, [isLoading, setIsRefreshing])

  // Chart configuration - must be defined before effects that use it
  const chartConfig = useMemo(() => {
    const viz = widget.visualization || {}
    
    
    return {
      chartType: viz.chartType || 'line',
      xAxis: viz.xAxis || {
        type: 'time',
        label: 'Time',
        timeZone: 'UTC'
      },
      yAxes: viz.yAxes || [{
        id: 'left',
        label: 'Value',
        unit: ''
      }],
      colors: viz.colors || ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'],
      strokeWidth: viz.strokeWidth || 2,
      legend: viz.legend || {
        show: true,
        position: 'bottom'
      },
      tooltip: viz.tooltip || {
        show: true
      },
      zoom: viz.zoom || {
        enabled: true,
        resetOnNewData: false
      },
      interpolateNulls: viz.interpolateNulls || false,
      showDataPoints: viz.showDataPoints !== undefined ? viz.showDataPoints : true, // Default to true with small data points
      connectNulls: viz.connectNulls !== undefined ? viz.connectNulls : true // Default to true for better continuity
    }
  }, [widget.visualization])

  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  
  const scrollToStart = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ left: 0, behavior: 'smooth' })
    }
  }
  
  const scrollToEnd = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ left: scrollContainerRef.current.scrollWidth, behavior: 'smooth' })
    }
  }

  // Create and pass legend component to parent
  React.useEffect(() => {
    if (onLegendRender && chartConfig.legend.show && dataStreams.length > 0) {
      
      const legendComponent = (
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          height: '100%',
          maxHeight: '60px', // Limit height to match header
          width: '100%',
          alignItems: 'center'
        }}>
          {/* Hide scrollbar but keep scroll functionality */}
          <style>{`
            .legend-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .legend-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .legend-nav-btn {
              background: rgba(0,0,0,0.05);
              border: none;
              border-radius: 2px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 20px;
              height: 20px;
              color: rgba(0,0,0,0.6);
              font-size: 10px;
              transition: all 0.2s ease;
            }
            .legend-nav-btn:hover {
              background: rgba(0,0,0,0.1);
              color: rgba(0,0,0,0.8);
            }
          `}</style>
          
          {/* Left arrow - scroll to start */}
          <button 
            className="legend-nav-btn"
            onClick={scrollToStart}
            style={{ marginRight: '4px' }}
          >
            ‹
          </button>
          
          {/* Scrollable Legend Items */}
          <div 
            ref={scrollContainerRef}
            style={{
              flex: 1,
              overflowX: 'auto',
              overflowY: 'hidden',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              gap: '8px',
              alignItems: 'center'
            }}
            className="legend-scrollbar"
          >
            {dataStreams.map((stream, index) => (
              <div
                key={`${stream.id}_${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  opacity: hiddenSeries.has(stream.id) ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                  backgroundColor: 'transparent',
                  fontSize: '10px',
                  minHeight: '16px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
                onClick={() => handleLegendClick(stream.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = hiddenSeries.has(stream.id) ? '#f0f0f0' : '#e6f3ff'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: chartConfig.colors[index % chartConfig.colors.length],
                    flexShrink: 0,
                    border: hiddenSeries.has(stream.id) ? '1px solid #ddd' : 'none',
                    transform: hiddenSeries.has(stream.id) ? 'scale(0.7)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    opacity: hiddenSeries.has(stream.id) ? 0.4 : 1
                  }}
                />
                <span style={{
                  color: hiddenSeries.has(stream.id) ? '#888' : '#333',
                  fontWeight: '500',
                  textDecoration: hiddenSeries.has(stream.id) ? 'line-through' : 'none',
                  transition: 'all 0.2s ease',
                  lineHeight: '1.2'
                }}>
                  {stream.name}
                </span>
              </div>
            ))}
          </div>
          
          {/* Right arrow - scroll to end */}
          <button 
            className="legend-nav-btn"
            onClick={scrollToEnd}
            style={{ marginLeft: '4px' }}
          >
            ›
          </button>
        </div>
      )
      onLegendRender(legendComponent)
    } else if (onLegendRender) {
      onLegendRender(null)
    }
  }, [onLegendRender, chartConfig.legend.show, dataStreams, hiddenSeries, handleLegendClick])
  
  // Retry function for user-triggered refresh
  const handleRetry = useCallback(() => {
    refetch()
  }, [refetch])

  // Calculate data coverage for display (MOVED BEFORE CONDITIONAL RETURNS TO FIX HOOKS ORDER)
  const dataCoverage = useMemo(() => {
    if (!dataStreams.length || !dataStreams[0].data.length) return null;
    
    const stream = dataStreams[0];
    const totalPoints = stream.data.length;
    const nonNullPoints = stream.data.filter(point => point.value !== null).length;
    const coveragePercent = totalPoints > 0 ? Math.round((nonNullPoints / totalPoints) * 100) : 0;
    
    return {
      totalPoints,
      nonNullPoints,
      coveragePercent
    };
  }, [dataStreams]);

  // Show configuration prompt if no queries are set up  
  if (!widget.queries || widget.queries.length === 0) {
    return (
      <>
        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box textAlign="center" padding="l">
            <SpaceBetween size="m">
              <div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                  Configure Chart Data
                </div>
                <div style={{ color: '#5f6b7a' }}>
                  This chart needs to be configured with data sources to display time series data.
                </div>
              </div>
              {isEditing && (
                <div style={{ color: '#879596', fontSize: '14px' }}>
                  Click the <strong>Configure</strong> option in the widget menu to add data sources.
                </div>
              )}
            </SpaceBetween>
          </Box>
        </div>

        {/* Chart Configuration Modal */}
        {showConfig && (
          <ChartWidgetConfig
            visible={showConfig}
            widget={widget}
            onConfirm={(updatedWidget) => {
              if (onEdit) {
                onEdit(updatedWidget)
              }
              setShowConfig(false)
            }}
            onDismiss={() => setShowConfig(false)}
          />
        )}
      </>
    )
  }

  if (error) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert 
          type="error" 
          statusIconAriaLabel="Error"
          action={
            <Button onClick={handleRetry} loading={isLoading}>
              Retry
            </Button>
          }
        >
          <div>Failed to load chart data</div>
          <div style={{ fontSize: '14px', marginTop: '8px' }}>
            {error?.message || 'Unknown error occurred'}
          </div>
        </Alert>
      </div>
    )
  }


  // Handle unconfigured widget (no valid queries)
  if (!widget.queries || widget.queries.length === 0 || siteWiseQueries.length === 0) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
        border: '2px dashed #dee2e6',
        borderRadius: '4px',
        color: '#6c757d'
      }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚙️</div>
          <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
            Widget Not Configured
          </div>
          <div style={{ fontSize: '12px', marginBottom: '12px' }}>
            Add data sources to display chart
          </div>
          {isEditing && (
            <Button 
              variant="primary" 
              size="small"
              onClick={() => setShowConfig(true)}
            >
              Configure Widget
            </Button>
          )}
        </div>
      </div>
    )
  }

  // Show loading state
  if (isLoading) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SpaceBetween size="m" direction="vertical" alignItems="center">
          <Spinner size="large" />
          <Box textAlign="center">
            <div style={{ fontSize: '14px', color: '#5f6b7a' }}>
              Loading chart data...
            </div>
            <div style={{ fontSize: '12px', color: '#879596', marginTop: '4px' }}>
              Querying {siteWiseQueries.length} data sources from SiteWise
            </div>
          </Box>
        </SpaceBetween>
      </div>
    )
  }

  return (
    <>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        
        
        {/* Chart Container - Full Width */}
        <div style={{ 
          flex: 1, 
          width: '100%', 
          minHeight: '120px',
          border: '1px solid #e1e5e9',
          borderRadius: '6px',
          margin: '8px',
          backgroundColor: '#fafbfc'
        }}>
          <SimpleChart
            dataStreams={dataStreams}
            config={chartConfig}
            showGrid={true}
            onTimeRangeChange={handleChartZoom}
            enableZoom={true}
            hiddenSeries={hiddenSeries}
            onSeriesToggle={handleSeriesToggle}
          />
        </div>
      </div>

      {/* Chart Configuration Modal */}
      {showConfig && (
        <ChartWidgetConfig
          visible={showConfig}
          widget={widget}
          onConfirm={(updatedWidget) => {
            if (onEdit) {
              onEdit(updatedWidget)
            }
            setShowConfig(false)
          }}
          onDismiss={() => setShowConfig(false)}
        />
      )}
    </>
  )
}